/**
 * Action Button Component
 * Standardized buttons for common actions across the app
 * Provides consistent styling, icons, and loading states
 */

import React from "react";
import {
  Button,
  ButtonProps,
  CircularProgress,
  Tooltip,
  IconButton,
  IconButtonProps,
} from "@mui/material";
import {
  Add as AddIcon,
  Save as SaveIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Cancel as CancelIcon,
  FileDownload as ExportIcon,
  FileUpload as ImportIcon,
  Refresh as RefreshIcon,
  Send as SendIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  ArrowBack as BackIcon,
  Visibility as ViewIcon,
  ContentCopy as CopyIcon,
  Print as PrintIcon,
  FilterList as FilterIcon,
  MoreVert as MoreIcon,
} from "@mui/icons-material";

/**
 * Action types with pre-configured styling
 */
export type ActionType =
  | "add"
  | "save"
  | "delete"
  | "edit"
  | "cancel"
  | "export"
  | "import"
  | "refresh"
  | "send"
  | "confirm"
  | "close"
  | "back"
  | "view"
  | "copy"
  | "print"
  | "filter"
  | "more";

interface ActionConfig {
  icon: React.ReactElement;
  label: string;
  variant: "contained" | "outlined" | "text";
  color: ButtonProps["color"];
  loadingLabel?: string;
}

const actionConfigs: Record<ActionType, ActionConfig> = {
  add: {
    icon: <AddIcon />,
    label: "Add",
    variant: "contained",
    color: "primary",
  },
  save: {
    icon: <SaveIcon />,
    label: "Save",
    variant: "contained",
    color: "primary",
    loadingLabel: "Saving...",
  },
  delete: {
    icon: <DeleteIcon />,
    label: "Delete",
    variant: "outlined",
    color: "error",
    loadingLabel: "Deleting...",
  },
  edit: {
    icon: <EditIcon />,
    label: "Edit",
    variant: "outlined",
    color: "primary",
  },
  cancel: {
    icon: <CancelIcon />,
    label: "Cancel",
    variant: "text",
    color: "inherit",
  },
  export: {
    icon: <ExportIcon />,
    label: "Export",
    variant: "outlined",
    color: "primary",
    loadingLabel: "Exporting...",
  },
  import: {
    icon: <ImportIcon />,
    label: "Import",
    variant: "outlined",
    color: "primary",
    loadingLabel: "Importing...",
  },
  refresh: {
    icon: <RefreshIcon />,
    label: "Refresh",
    variant: "text",
    color: "primary",
  },
  send: {
    icon: <SendIcon />,
    label: "Send",
    variant: "contained",
    color: "primary",
    loadingLabel: "Sending...",
  },
  confirm: {
    icon: <CheckIcon />,
    label: "Confirm",
    variant: "contained",
    color: "success",
  },
  close: {
    icon: <CloseIcon />,
    label: "Close",
    variant: "text",
    color: "inherit",
  },
  back: {
    icon: <BackIcon />,
    label: "Back",
    variant: "text",
    color: "inherit",
  },
  view: {
    icon: <ViewIcon />,
    label: "View",
    variant: "text",
    color: "primary",
  },
  copy: {
    icon: <CopyIcon />,
    label: "Copy",
    variant: "text",
    color: "primary",
  },
  print: {
    icon: <PrintIcon />,
    label: "Print",
    variant: "outlined",
    color: "primary",
  },
  filter: {
    icon: <FilterIcon />,
    label: "Filter",
    variant: "outlined",
    color: "primary",
  },
  more: {
    icon: <MoreIcon />,
    label: "More",
    variant: "text",
    color: "inherit",
  },
};

export interface ActionButtonProps
  extends Omit<ButtonProps, "variant" | "color" | "startIcon" | "action"> {
  /** Pre-configured action type */
  action: ActionType;
  /** Custom label (overrides default) */
  label?: string;
  /** Whether action is in progress */
  isLoading?: boolean;
  /** Custom loading label */
  loadingLabel?: string;
  /** Hide the icon */
  hideIcon?: boolean;
  /** Icon-only mode (no label) */
  iconOnly?: boolean;
  /** Tooltip text (shown on hover, required for icon-only) */
  tooltip?: string;
  /** Override variant */
  variant?: ButtonProps["variant"];
  /** Override color */
  color?: ButtonProps["color"];
}

export const ActionButton: React.FC<ActionButtonProps> = ({
  action,
  label,
  isLoading = false,
  loadingLabel,
  hideIcon = false,
  iconOnly = false,
  tooltip,
  variant,
  color,
  disabled,
  size = "medium",
  ...buttonProps
}) => {
  const config = actionConfigs[action];
  const resolvedLabel = label || config.label;
  const resolvedLoadingLabel = loadingLabel || config.loadingLabel || "Loading...";
  const resolvedVariant = variant || config.variant;
  const resolvedColor = color || config.color;

  const displayLabel = isLoading ? resolvedLoadingLabel : resolvedLabel;

  // Icon-only button
  if (iconOnly) {
    const iconButton = (
      <IconButton
        size={size as IconButtonProps["size"]}
        color={resolvedColor}
        disabled={disabled || isLoading}
        aria-label={resolvedLabel}
        {...(buttonProps as unknown as IconButtonProps)}
      >
        {isLoading ? (
          <CircularProgress size={size === "small" ? 16 : 20} color="inherit" />
        ) : (
          config.icon
        )}
      </IconButton>
    );

    if (tooltip) {
      return <Tooltip title={tooltip}>{iconButton}</Tooltip>;
    }
    return iconButton;
  }

  // Full button
  const button = (
    <Button
      variant={resolvedVariant}
      color={resolvedColor}
      size={size}
      disabled={disabled || isLoading}
      startIcon={
        hideIcon ? undefined : isLoading ? (
          <CircularProgress size={size === "small" ? 14 : 16} color="inherit" />
        ) : (
          config.icon
        )
      }
      {...buttonProps}
    >
      {displayLabel}
    </Button>
  );

  if (tooltip) {
    return (
      <Tooltip title={tooltip}>
        <span>{button}</span>
      </Tooltip>
    );
  }

  return button;
};

/**
 * Shorthand components for common actions
 */
export const AddButton: React.FC<Omit<ActionButtonProps, "action">> = (props) => (
  <ActionButton action="add" {...props} />
);

export const SaveButton: React.FC<Omit<ActionButtonProps, "action">> = (props) => (
  <ActionButton action="save" {...props} />
);

export const DeleteButton: React.FC<Omit<ActionButtonProps, "action">> = (props) => (
  <ActionButton action="delete" {...props} />
);

export const EditButton: React.FC<Omit<ActionButtonProps, "action">> = (props) => (
  <ActionButton action="edit" {...props} />
);

export const CancelButton: React.FC<Omit<ActionButtonProps, "action">> = (props) => (
  <ActionButton action="cancel" {...props} />
);

export const ExportButton: React.FC<Omit<ActionButtonProps, "action">> = (props) => (
  <ActionButton action="export" {...props} />
);

export const BackButton: React.FC<Omit<ActionButtonProps, "action">> = (props) => (
  <ActionButton action="back" {...props} />
);

export const RefreshButton: React.FC<Omit<ActionButtonProps, "action">> = (props) => (
  <ActionButton action="refresh" {...props} />
);

/**
 * Button group for form actions (Cancel + Save)
 */
export interface FormActionsProps {
  onCancel: () => void;
  onSave?: () => void;
  isSaving?: boolean;
  saveLabel?: string;
  cancelLabel?: string;
  saveDisabled?: boolean;
  /** Submit button type (use "submit" for form submission) */
  saveType?: "button" | "submit";
}

export const FormActions: React.FC<FormActionsProps> = ({
  onCancel,
  onSave,
  isSaving = false,
  saveLabel = "Save",
  cancelLabel = "Cancel",
  saveDisabled = false,
  saveType = "submit",
}) => (
  <>
    <CancelButton label={cancelLabel} onClick={onCancel} disabled={isSaving} />
    <SaveButton
      label={saveLabel}
      isLoading={isSaving}
      onClick={onSave}
      disabled={saveDisabled}
      type={saveType}
    />
  </>
);

export default ActionButton;
